import styled from 'styled-components';

const TIMELINE_FONT = "'Poppins', 'Open Sans', sans-serif";

export const TrackingWrapper = styled.div`
    box-sizing: border-box;
    position: relative;
    isolation: isolate;
    padding: 1.2rem 2.3rem 1rem;
    max-width: 632px;
    margin: 1rem auto 0;
    background: transparent;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow: visible;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 8px;
        background: #fff;
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
        z-index: -1;
    }

    &::before {
        content: '';
        position: absolute;
        top: -18px;
        left: 0;
        right: 0;
        height: 85px;
        border-radius: 16px 16px 0 0;
        background: #f8e5e0;
        z-index: -1;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        max-width: 100%;
        margin-top: 0.5rem;
        padding: 1.25rem 1rem 1.5rem;
        &::after {
            border-radius: 0;
            box-shadow: none;
        }
        &::before {
            display: none;
        }
    }
`;

export const TrackingHeader = styled.div`
    text-align: center;
    padding: 1rem 0 0.5rem;

    h2 {
        font-family: 'Raleway', sans-serif;
        font-size: 1.5rem;
        font-weight: 700;
        color: #222;
        margin: 0.75rem 0 0.25rem;
    }

    p {
        font-family: 'Open Sans', sans-serif;
        font-size: 0.95rem;
        color: #555;
        margin: 0;
    }
`;

export const TimelineContainer = styled.div`
    padding: 0.5rem 0 0.5rem 1.55rem;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        padding: 0.5rem 0 1.85rem 1.3rem;
    }
`;

export const TimelineStep = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    position: relative;
    min-height: 64px;
`;

export const StepIndicator = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    width: 20px;
    position: relative;
`;

export const StepCircle = styled.div<{ $completed: boolean }>`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Open Sans', sans-serif;
    font-size: 0.65rem;
    font-weight: 700;
    flex-shrink: 0;
    background: ${(props) => (props.$completed ? '#F5821F' : '#9A9A9A')};
    color: #fff;
    border: none;
    z-index: 1;
`;

export const StepLine = styled.div<{ $completed: boolean }>`
    width: 1px;
    flex: 1;
    min-height: 46px;
    margin: 8px 0;
    background: ${(props) => (props.$completed ? '#F5821F' : '#9A9A9A')};
`;

export const StepContent = styled.div`
    margin-left: 0.45rem;
    padding-bottom: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
`;

export const StepLabel = styled.span`
    font-family: ${TIMELINE_FONT};
    font-size: 0.95rem;
    font-weight: 500;
    color: #8C8C8C;
    line-height: 1.3;
`;

export const StepDate = styled.span`
    font-family: ${TIMELINE_FONT};
    font-size: 0.78rem;
    font-weight: 500;
    color: #8C8C8C;
    line-height: 1.3;
`;

export const BackLinkWrapper = styled.div`
    display: block;
    box-sizing: border-box;
    max-width: 780px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    padding-bottom: 0.5rem;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        max-width: 100%;
        padding: 0.5rem 1rem;
    }
`;

export const BackLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: none;
    border: none;
    cursor: pointer;
    font-family: 'Open Sans', sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    color: #333;
    padding: 0;

    svg {
        flex-shrink: 0;
    }

    &:hover {
        color: #000;
    }
`;

export const SummarySection = styled.div`
    box-sizing: border-box;
    max-width: 632px;
    width: 100%;
    margin: 0.75rem auto 0;
    padding: 1.25rem 2.3rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        max-width: 100%;
        padding: 1.25rem 1rem;
        border-radius: 0;
        box-shadow: none;
        border-top: 1px solid #e0e0e0;
    }
`;

export const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.35rem 0;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.15rem;
    }
`;

export const SummaryLabel = styled.span`
    font-family: 'Open Sans', sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    color: #555;
`;

export const SummaryValue = styled.span`
    font-family: 'Open Sans', sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    color: #222;
`;

export const SummaryValueRegular = styled(SummaryValue)`
    font-weight: 400;
`;

export const ErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2rem 1rem;
    gap: 1rem;

    p {
        font-family: 'Open Sans', sans-serif;
        font-size: 0.95rem;
        color: #555;
        line-height: 1.5;
        max-width: 460px;
        margin: 0;
    }
`;

export const InvalidContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2rem 1rem;

    p {
        font-family: 'Open Sans', sans-serif;
        font-size: 0.95rem;
        color: #999;
        margin: 0;
    }
`;
